import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from "jwt-decode";
import { format } from 'date-fns'; 

const DashboardTab = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coachName, setCoachName] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loadingTx, setLoadingTx] = useState(true);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";

  const getGreeting = (t) => {
    const now = new Date();
    const hour = now.getHours();
  
    if (hour < 12) return t('greeting.morning');
    if (hour < 18) return t('greeting.afternoon');
    return t('greeting.evening');
  };
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setRole("unauthenticated");
      setLoading(false);
      return;
    }

    const decodedToken = jwtDecode(token);

    const { userId, role } = decodedToken;
    setRole(role);

    if (!userId) {
      setRole("unauthenticated");
      setLoading(false);
      return;
    }

    fetch(`${API_URL}account/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => {
        if (!res.ok) {
          console.error("Failed to fetch user data:", res.statusText);
          throw new Error("Failed to fetch user data");
          
        }
        return res.json();
      })
      .then((data) => {
        setUser(data);
        setCoachName(data.firstName + ` ` + data.lastName)
      })
      .catch((err) => {
        console.error("Error fetching user info:", err);
        setRole("unauthenticated");
        setLoading(false);
      });
  }, [API_URL]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_URL}customers`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => {
        if (!res.ok) {
          console.error("Failed to fetch clients:", res.statusText);
          throw new Error("Failed to fetch clients");
        }
        return res.json();
      })
      .then((data) => {
        setClients(data);
      })
      .catch((err) => {
        console.error("Error fetching clients:", err);
      })
      .finally(() => setLoading(false));
  }, [API_URL]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_URL}transactions`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => {
        if (!res.ok) {
          console.error("Failed to fetch transactions:", res.statusText);
          throw new Error("Failed to fetch transactions");
        }
        return res.json();
      })
      .then(data => {
        setTransactions(data);
      })
      .catch(err => console.error("Error fetching transactions:", err))
      
      .finally(() => setLoadingTx(false));
  }, [API_URL]);

  useEffect(() => {
    if (!user) return;
  
    const coachLN = user.lastName;
    const today = format(new Date(), 'yyyy-MM-dd');
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
  
    const consultsPromise = fetch(
      `${API_URL}consultations/booked/${coachLN}/${today}`,
      { headers }
    ).then(r => r.ok ? r.json() : Promise.reject("No consults"));
  
    const breaksPromise = fetch(
      `${API_URL}breaks/coach/${encodeURIComponent(coachLN)}/${today}`,
      { headers }
    ).then(r => r.ok ? r.json() : Promise.reject("No breaks"));
  
    Promise.allSettled([consultsPromise, breaksPromise])
      .then(results => {
        const items = [];
  
        if (results[0].status === "fulfilled") {
          results[0].value.forEach(c =>
            items.push({
              type: "consultation",
              time: c.time,
              client: `${c.firstName} ${c.lastName}`
            })
          );
        }
  
        if (results[1].status === "fulfilled") {
          results[1].value.forEach(b =>
            items.push({
              type: "break",
              time: b.time,
              note: b.reason
            })
          );
        }
  
        items.sort((a, b) => a.time.localeCompare(b.time));
        setScheduleItems(items);
      })
      .catch(err => console.error("Unexpected schedule error:", err))
      .finally(() => setLoadingSchedule(false));
  }, [user, API_URL]);

  useEffect(() => {
 
    if (!coachName || transactions.length === 0) return;
       
    const thisMonth = new Date().getMonth();
    const filteredTx = transactions.filter(tx => {
      const txCoach = tx.description?.toLowerCase().includes(`coach: ${coachName.toLowerCase()}`);
      const txDate = new Date(tx.created * 1000);
      return txCoach && txDate.getMonth() === thisMonth;
    });
    setFiltered(filteredTx);
  }, [transactions, coachName]);

  if (loading || loadingTx) return <p>Loading dashboard... ☕✨</p>;
  const coachFullName = user ? `${user.firstName} ${user.lastName}` : "";
  const clientCount = role === "admin"
    ? clients.length
    : clients.filter((client) => client.assignedCoach === coachFullName).length;

  const totalSales = filtered.reduce((sum, tx) => sum + tx.amount_total, 0);
  const totalSalesCount = filtered.length;

/*   const sortedByDate = [...filtered].sort((a, b) => b.created - a.created);
const latestTx = sortedByDate.length ? sortedByDate[0] : null; */


  const productCounts = {};
  filtered.forEach(tx => {
    const summary = tx.metadata?.cartSummary || '';
    const items = summary.split(',');
    items.forEach(item => {
      if (!productCounts[item]) productCounts[item] = 0;
      productCounts[item]++;
    });
  });

  const mostPopular = Object.entries(productCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const greeting = getGreeting(t);

 

  return (
    <Container>
      <TitleDiv>
        <GreetingBar>{greeting}, {user?.firstName}!</GreetingBar>
      </TitleDiv>
    
    <MainGrid>
<LeftSide>
      <LeftColumn>
        <SectionTitle>{t("dashboard.totalClients")}</SectionTitle>
        <BigNumber>{clientCount}</BigNumber>
      </LeftColumn>

      <CenterColumn>
        <SectionTitle>📅 {t("dashboard.today")}</SectionTitle>
      <Scrolldiv>
        {loadingSchedule ? (
          <p>Loading…</p>
        ) : scheduleItems.length === 0 ? (
          <p>No appointments or breaks today.</p>
        ) : (
         <ul>
  {scheduleItems.map((item, i) =>
    item.type === 'consultation' ? (
      <ConsultationItem key={i}>
        <strong>{item.time}</strong> – Consultation: {item.client}
      </ConsultationItem>
    ) : (
      <BreakItem key={i}>
        <strong>{item.time}</strong> – Break: {item.note}
      </BreakItem>
    )
  )}
</ul>
        )}
  </Scrolldiv>      
      </CenterColumn>
</LeftSide>

      <RightColumn>
        <SectionTitle>{t("dashboard.sales")}</SectionTitle>
        <Stat>{t("dashboard.orders")} {totalSalesCount}</Stat>
        <Stat>{t("dashboard.total")} ${totalSales / 100}</Stat>
        {mostPopular && <Stat>🔥 Top Product: {mostPopular}</Stat>}
{Object.keys(productCounts).length > 0 && (
  <Stat><Latest>
    <SectionTitle><strong>🛒 {t("dashboard.latest")}</strong></SectionTitle>
    <ul>
      {Object.entries(productCounts).map(([item]) => (
        <li key={item}>{item.trim()}</li>
      ))}
    </ul></Latest>
  </Stat>
)}
      </RightColumn>
    </MainGrid>
  </Container>
  );
};

export default DashboardTab;

// Container for the entire dashboard
const Container = styled.div`
  font-family: 'Poppins', sans-serif;
`;

// Title section
const TitleDiv = styled.div`

`;

// Greeting bar with responsive font size
const GreetingBar = styled.h2`
  text-shadow: 2px 3px 3px rgba(148, 163, 135, 1.1);
  font-size: 3rem;
  color: rgb(241, 170, 160);
  margin-bottom: 2rem;
  text-align: end;
  font-family: 'Great Vibes', cursive;

  @media (max-width: 600px) {
    font-size: 2rem;
    text-align: center;
  }
`;

// Main grid layout with responsive behavior
const MainGrid = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

// Left side container with fixed width and responsive max-width
const LeftSide = styled.div`
  display: flex;
  flex-direction: column;
  width: 700px;
  max-width: 100%;
`;

// Left column styling
const LeftColumn = styled.div`
  background: rgba(148, 163, 135, 1.1);
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(255, 182, 193, 0.2);
  text-align: center;
`;

// Center column with gradient background
const CenterColumn = styled.div`
  margin-top: 5px;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  background: linear-gradient(
    122deg,
    rgba(148, 163, 135, 1.1) 0%,
    rgba(186, 204, 171, 1.1) 43%,
    rgba(246, 187, 155, 1.1) 83%,
    rgba(217, 121, 97, 1.1) 100%
  );
`;

// Scrollable div for schedule items
const Scrolldiv = styled.div`
  height: 199px;
  overflow-y: auto;
  padding: 1rem;
  border: 1px solid #ccc;
  background-color: rgba(255, 255, 255, 0.3);
  max-width: 100%;
`;

// Right column with fixed width and responsive max-width
const RightColumn = styled.div`
  background: rgb(233, 174, 159);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(173, 216, 230, 0.2);
  width: 620px;
  max-width: 100%;
`;

// Section title styling
const SectionTitle = styled.h3`
  text-shadow: 3px 2px 5px black;
  font-weight: 600;
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color:rgb(255, 255, 255);
  text-align: center;
  font-family: 'Great Vibes', cursive;
`;

// Big number display
const BigNumber = styled.div`
  font-size: 100px;
  font-weight: bold;
  color: rgb(255, 255, 255);
`;

// Statistic text
const Stat = styled.p`
  margin: 0.5rem 0;
  font-weight: 500;
`;

// Latest items container
const Latest = styled.div`
  margin-top: 1rem;
 background: rgba(255, 255, 255, 0.3);
  padding: 0.8rem;
  border-radius: 8px;
  height: 380px;
`;

// Consultation item styling
const ConsultationItem = styled.li`
  background-color: rgb(255, 255, 255);
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  border-left: 10px solid rgb(127, 156, 101);
  list-style: none;
  box-shadow: 2px 2px 5px black;
`;

// Break item styling
const BreakItem = styled.li`
  background-color: rgb(255, 255, 255);
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  border-left: 10px solid rgb(202, 51, 51);
  list-style: none;
  box-shadow: 2px 2px 5px black;
`;

